import { create } from "zustand"

import type { WorkflowChatMessage } from "@/api/models"

type WorkflowAiChatState = {
  activeWorkflowId: string | null
  isOpen: boolean
  panelWidth: number
  messages: WorkflowChatMessage[]
  isStreaming: boolean
  error: string | null
  pendingPrompt: string | null
  setActiveWorkflow: (workflowId: string | null, messages: WorkflowChatMessage[]) => void
  setIsOpen: (open: boolean) => void
  setPanelWidth: (width: number) => void
  setIsStreaming: (streaming: boolean) => void
  setError: (error: string | null) => void
  setPendingPrompt: (prompt: string | null) => void
  clearPendingPrompt: () => void
  addMessage: (message: WorkflowChatMessage) => void
  updateMessageContent: (messageId: string, content: string) => void
  clear: () => void
}

const DEFAULT_PANEL_WIDTH = 420
const MIN_PANEL_WIDTH = 320
const MAX_PANEL_WIDTH = 720

function clampPanelWidth(width: number): number {
  return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width))
}

// The AI Builder panel is a side drawer. On small screens it would otherwise
// open over the canvas on first load, so default it closed below the lg
// breakpoint (≥1024px keeps the desktop "open by default" behaviour).
const DEFAULT_AI_PANEL_OPEN = typeof window === "undefined" || window.innerWidth >= 1024

export const useWorkflowAiChatStore = create<WorkflowAiChatState>((set, get) => ({
  activeWorkflowId: null,
  isOpen: DEFAULT_AI_PANEL_OPEN,
  panelWidth: DEFAULT_PANEL_WIDTH,
  messages: [],
  isStreaming: false,
  error: null,
  pendingPrompt: null,
  setActiveWorkflow: (workflowId, messages) => {
    set({
      activeWorkflowId: workflowId,
      messages,
      error: null,
      isStreaming: false,
    })
  },
  setIsOpen: (open) => set({ isOpen: open }),
  setPanelWidth: (width) => set({ panelWidth: clampPanelWidth(width) }),
  setIsStreaming: (streaming) => set({ isStreaming: streaming }),
  setError: (error) => set({ error }),
  setPendingPrompt: (prompt) => set({ pendingPrompt: prompt }),
  clearPendingPrompt: () => set({ pendingPrompt: null }),
  addMessage: (message) => set({ messages: [...get().messages, message] }),
  updateMessageContent: (messageId, content) =>
    set({
      messages: get().messages.map((message) =>
        message.id === messageId ? { ...message, content } : message
      ),
    }),
  clear: () => set({ messages: [], error: null, isStreaming: false, pendingPrompt: null }),
}))
